import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { AnimationDuration, getAnimationAppearDisappear } from '@examdojo/animation';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { IconComponent } from '@examdojo/core/icon';
import { SwiperComponent, SwiperSlideDirective } from '@examdojo/core/swiper';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { UserStoreModel } from '@examdojo/models/user';
import { PlatformService } from '@examdojo/platform';
import { ImageLocation } from '@examdojo/question';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { ImagePreviewDirective } from '@examdojo/ui/image-preview';
import { assertNonNullable } from '@examdojo/util/assert';
import { IonBadge, IonSpinner, IonThumbnail } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter, from, switchMap } from 'rxjs';
import { SwiperOptions } from 'swiper/types/swiper-options';
import { ReviewItemStoreModel } from '../../review-item';
import { ReviewSolutionImageStoreModel } from '../review-solution-image.model';
import { ReviewSolutionImageQuery } from '../review-solution-image.query';
import { ReviewSolutionImageService } from '../review-solution-image.service';
import { ReviewSolutionLinkDialogService } from '../solution-link-dialog/review-solution-link-dialog.service';

@Component({
  selector: 'dojo-review-solution-images-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './review-solution-images-list.component.html',
  styleUrls: ['./review-solution-images-list.component.scss'],
  animations: [getAnimationAppearDisappear({ enter: AnimationDuration.Short })],
  imports: [
    IonThumbnail,
    ButtonComponent,
    SwiperComponent,
    SwiperSlideDirective,
    IonBadge,
    IconComponent,
    IconButtonComponent,
    ImagePreviewDirective,
    IonSpinner,
    TranslocoPipe,
  ],
  providers: [ReviewSolutionLinkDialogService],
})
export class ReviewSolutionImagesListComponent {
  constructor(
    private readonly solutionService: ReviewSolutionImageService,
    private readonly reviewSolutionImageService: ReviewSolutionImageService,
    private readonly destroyRef: DestroyRef,
    private readonly reviewSolutionLinkDialogService: ReviewSolutionLinkDialogService,
    private readonly reviewSolutionImageQuery: ReviewSolutionImageQuery,
    private readonly platformService: PlatformService,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
  ) {}

  readonly swiperOptions: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 8,
    freeMode: true,
    mousewheel: {
      forceToAxis: true,
    },
  };

  readonly userId = input.required<UserStoreModel['id']>();
  readonly reviewSession = input.required<ReviewItemStoreModel['id']>();

  readonly fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  readonly state = connectState({
    allImages: this.reviewSolutionImageQuery.selectAllEntities(),
    isTestingFlagEnabled: this.featureFlagService.select(ExamDojoFeatureFlag.Testing),
  });

  readonly isDesktop = this.platformService.isDesktop;

  readonly ImageLocation = ImageLocation;

  readonly hasMediaCaptureSupport = computed(() => {
    const inputRef = this.fileInputRef();
    return inputRef && 'capture' in inputRef.nativeElement;
  });

  addNewSolutionImage(forceNativeCapture = false) {
    if (this.hasMediaCaptureSupport() || forceNativeCapture) {
      const fileInput = this.fileInputRef()?.nativeElement;
      assertNonNullable(fileInput, 'fileInputRef');
      fileInput.click();
      return;
    }

    return from(this.solutionService.addNewSolutionImage())
      .pipe(
        filter(Boolean),
        switchMap((image) => {
          return this.solutionService.saveSolutionImage(this.userId(), this.reviewSession(), image);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  addNewSolutionImageWithQRCode() {
    this.reviewSolutionLinkDialogService.openDialog();
  }

  removeSolutionImage(image: ReviewSolutionImageStoreModel) {
    if (image.location === ImageLocation.Remote) {
      this.reviewSolutionImageService
        .removeReviewSolutionImage(image.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  saveInputImage($event: Event) {
    const file = ($event.target as HTMLInputElement).files?.[0];
    if (!file) {
      return;
    }

    this.solutionService
      .saveSolutionImage(this.userId(), this.reviewSession(), file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
